import { useNavigate } from 'react-router-dom';
import { useStore } from '@/store';
import { manifest } from '@/data';
import { importCharacterJson } from '@/lib/serialize';
import { CelestialSigil } from '@/components/ui/CelestialSigil';
import { SectionEyebrow } from '@/components/ui/SectionEyebrow';
import { PathCard, type PathKind } from '@/components/ui/PathCard';
import { SpellCardShowcase } from '@/components/ui/SpellCardShowcase';
import { useReveal } from '@/hooks/useReveal';

export function HomePage() {
  const characters = useStore((s) => s.characters);
  const activeId = useStore((s) => s.activeId);
  const addCharacter = useStore((s) => s.addCharacter);
  const newCharacter = useStore((s) => s.newCharacter);
  const activateCharacter = useStore((s) => s.activateCharacter);
  const removeCharacter = useStore((s) => s.removeCharacter);
  const duplicateCharacter = useStore((s) => s.duplicateCharacter);
  const navigate = useNavigate();

  const pathsRef = useReveal<HTMLDivElement>();
  const gridRef = useReveal<HTMLDivElement>();
  const showcaseCopyRef = useReveal<HTMLDivElement>();
  const showcaseCardRef = useReveal<HTMLDivElement>();

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const c = importCharacterJson(text);
      addCharacter(c);
      navigate(`/create/${c.type}`);
    } catch (err) {
      alert(`Couldn't import that file: ${(err as Error).message}`);
    } finally {
      e.target.value = '';
    }
  }

  function start(type: PathKind) {
    newCharacter(type);
    navigate(`/create/${type}`);
  }

  function openCharacter(id: string, type: string) {
    activateCharacter(id);
    navigate(`/create/${type}`);
  }

  // Most-recently-updated first.
  const sortedLibrary = [...characters].sort((a, b) =>
    (b.char.updatedAt || '').localeCompare(a.char.updatedAt || '')
  );

  return (
    <>
      <CelestialSigil />

      <section className="relative grid min-h-screen place-items-center overflow-hidden px-6 pt-28 pb-24">
        <div className="relative z-[2] mx-auto max-w-[880px] text-center">
          <div
            className="pointer-events-none absolute -z-[1]"
            style={{
              inset: '10% 5%',
              background:
                'radial-gradient(ellipse 60% 55% at 50% 55%, rgba(7,5,10,0.55) 0%, rgba(7,5,10,0.2) 55%, rgba(7,5,10,0) 80%)',
            }}
          />
          <div
            className="mb-7 font-display uppercase tracking-[0.45em] text-[12px] text-gold-400 opacity-0"
            style={{ animation: 'fade-up 1s 0.2s forwards' }}
          >
            · Definitive Edition ·
          </div>
          <h1
            className="mb-2 font-serif font-normal leading-none tracking-[0.01em] text-parchment-50 opacity-0"
            style={{
              fontSize: 'clamp(54px, 9vw, 124px)',
              textShadow: '0 2px 24px rgba(0,0,0,0.8), 0 0 40px rgba(201,165,92,0.15)',
              animation: 'fade-up 1.2s 0.4s forwards',
            }}
          >
            Ars <em className="italic text-gold-300">Magica</em>
          </h1>
          <p
            className="mx-auto mb-10 max-w-[620px] font-sans font-light text-parchment-100 opacity-0"
            style={{
              fontSize: 'clamp(15px, 1.5vw, 19px)',
              animation: 'fade-up 1s 0.7s forwards',
            }}
          >
            A living <span className="text-gold-300 tracking-[0.08em]">grimoire</span> for the wizards
            of the Order of Hermes. Forge a magus, an adventuring companion, or a loyal grog —
            shareable by link, bound to no server.
          </p>
          <div
            className="flex flex-wrap justify-center gap-4 opacity-0"
            style={{ animation: 'fade-up 1s 0.9s forwards' }}
          >
            <button className="btn-primary" onClick={() => start('magus')}>
              Begin Your Saga
            </button>
            <a href="#paths" className="btn-secondary">
              Explore the Paths
            </a>
          </div>

          {sortedLibrary.length > 0 && (
            <div
              className="mx-auto mt-12 w-full max-w-xl opacity-0"
              style={{ animation: 'fade-up 1s 1.1s forwards' }}
            >
              <div className="mb-3 text-center font-display text-[10px] uppercase tracking-[0.3em] text-gold-500">
                Your Saga · {characters.length} {characters.length === 1 ? 'character' : 'characters'}
              </div>
              <ul className="space-y-2">
                {sortedLibrary.slice(0, 6).map((e) => (
                  <li
                    key={e.id}
                    className={`flex flex-wrap items-center justify-between gap-3 rounded-lg-ornate
                                border px-4 py-3 text-left transition-colors
                                ${e.id === activeId
                                  ? 'border-gold-400/60 bg-gold-400/10'
                                  : 'border-gold-400/25 bg-ink-900/60 hover:border-gold-400/50'}`}
                  >
                    <button
                      onClick={() => openCharacter(e.id, e.char.type)}
                      className="flex-1 text-left"
                    >
                      <div className="font-serif text-lg text-parchment-50">
                        {e.char.name || 'Unnamed'}{' '}
                        <span className="chip ml-1">{e.char.type}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-parchment-100/50">
                        updated {new Date(e.char.updatedAt).toLocaleDateString()}
                      </div>
                    </button>
                    <div className="flex gap-1.5">
                      <button
                        className="btn-quiet !py-1 !px-3 text-[11px]"
                        onClick={() => {
                          const nid = duplicateCharacter(e.id);
                          if (nid) navigate(`/create/${e.char.type}`);
                        }}
                        aria-label={`Duplicate ${e.char.name || 'character'}`}
                      >
                        Duplicate
                      </button>
                      <button
                        className="btn-quiet !py-1 !px-3 text-[11px]"
                        onClick={() => {
                          if (
                            confirm(
                              `Remove "${e.char.name || 'Unnamed'}" from your library?`
                            )
                          ) {
                            removeCharacter(e.id);
                          }
                        }}
                        aria-label={`Remove ${e.char.name || 'character'}`}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
                {sortedLibrary.length > 6 && (
                  <li className="text-center text-[11px] text-parchment-100/50">
                    + {sortedLibrary.length - 6} more (scroll down to start a new saga)
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        <div
          className="pointer-events-none absolute bottom-8 left-1/2 -translate-x-1/2 font-display
                     text-[11px] uppercase tracking-[0.4em] text-gold-400 opacity-60"
        >
          Descend
          <span
            className="mx-auto mt-3 block h-[42px] w-px animate-scroll-pulse"
            style={{
              background: 'linear-gradient(to bottom, #c9a55c, transparent)',
            }}
          />
        </div>
      </section>

      <section id="paths" className="relative z-[2] mx-auto max-w-[1280px] px-6 py-24 md:py-32">
        <div ref={pathsRef} className="reveal">
          <SectionEyebrow>I · The Three Paths</SectionEyebrow>
          <h2 className="display-title">
            Choose your <em>calling</em>
          </h2>
          <p className="section-lede">
            Every saga begins with a vessel. Iron-bound warden, worldly adventurer, or theurgic
            wielder of the Arts — each path shapes the story differently.
          </p>
        </div>

        <div ref={gridRef} className="reveal grid gap-7 md:grid-cols-3 [perspective:1400px]">
          <PathCard
            kind="grog"
            title="The Grog"
            subtitle="Warden · Shield-Bearer"
            desc="A sworn defender of the covenant. Quick to forge, rich in martial detail — ideal for your first saga, or when the covenfolk need a reliable blade at the gate."
            stats={[
              { value: '45', label: 'Pts' },
              { value: 'III', label: 'Abilities' },
              { value: 'V', label: 'Virtues' },
            ]}
            sigil={
              <>
                <circle cx="50" cy="50" r="44" />
                <path d="M50 18 L72 74 L28 74 Z" />
                <path d="M50 82 L50 18 M18 50 L82 50" />
                <circle cx="50" cy="50" r="6" />
              </>
            }
            onPick={() => start('grog')}
          />
          <PathCard
            kind="companion"
            title="The Companion"
            subtitle="Adventurer · Scholar"
            desc="A singular mortal with uncommon gifts. Faeries, holy priests, cunning spies and hedge-wise healers alike. No Hermetic arts, but deep in worldly lore."
            stats={[
              { value: '75', label: 'Pts' },
              { value: 'VII', label: 'Abilities' },
              { value: 'X', label: 'Virtues' },
            ]}
            sigil={
              <>
                <circle cx="50" cy="50" r="44" />
                <circle cx="50" cy="50" r="30" />
                <path d="M20 50 Q50 10 80 50 Q50 90 20 50 Z" />
                <circle cx="50" cy="50" r="4" />
              </>
            }
            onPick={() => start('companion')}
          />
          <PathCard
            kind="magus"
            title="The Magus"
            subtitle="Archmage · Hermetic"
            desc="A wielder of the fifteen Arts — Creo, Perdo, Ignem, Vim and their kin. Choose a House, inherit its Mystery, shape laboratories and wards as you will."
            stats={[
              { value: '240', label: 'Pts' },
              { value: 'XV', label: 'Arts' },
              { value: 'XII', label: 'Houses' },
            ]}
            sigil={
              <>
                <circle cx="50" cy="50" r="44" />
                <path d="M50 10 L62 42 L94 42 L68 62 L78 94 L50 74 L22 94 L32 62 L6 42 L38 42 Z" />
                <circle cx="50" cy="50" r="16" />
                <circle cx="50" cy="50" r="4" />
              </>
            }
            onPick={() => start('magus')}
          />
        </div>
      </section>

      <section className="relative z-[2] mx-auto max-w-[1180px] px-6 py-24 md:py-28">
        <div className="grid items-center gap-20 md:grid-cols-2">
          <div ref={showcaseCopyRef} className="reveal">
            <SectionEyebrow start>II · Hermetic Theory</SectionEyebrow>
            <h2 className="display-title is-start">
              The <em>Arts</em> bound to parchment.
            </h2>
            <p className="mb-4 text-base leading-[1.7] text-parchment-100/70">
              Every spell, virtue, flaw and ability from the Definitive Edition is catalogued and
              cross-referenced — not as raw rules, but as an illuminated reference, tuned for the
              storyteller's table.
            </p>
            <ul className="mt-7 flex flex-col gap-3.5 text-[15px] text-parchment-100/85">
              {[
                'Live technique + form calculation, no math on scratch paper.',
                'Paraphrased tooltips that respect the open license.',
                'One-click share — the URL itself carries your character, no server required.',
                'Export your magus to a signed .arsmagica.json for the ages.',
              ].map((li) => (
                <li key={li} className="flex items-start gap-3.5">
                  <span className="mt-1.5 block h-2 w-2 rotate-45 border border-gold-400" />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
          </div>
          <div ref={showcaseCardRef} className="reveal">
            <SpellCardShowcase />
          </div>
        </div>
      </section>

      <section className="relative z-[2] mx-auto max-w-[1180px] px-6 pb-24">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg-ornate
                        border border-gold-400/20 bg-ink-800/50 px-6 py-5">
          <div className="font-display text-[11px] uppercase tracking-[0.25em] text-gold-400">
            Archives · {manifest.counts.virtues} virtues · {manifest.counts.flaws} flaws ·{' '}
            {manifest.counts.abilities} abilities · {manifest.counts.spells} spells
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="btn-quiet cursor-pointer !py-2 !px-4">
              Import JSON
              <input
                type="file"
                accept="application/json,.json"
                onChange={handleImport}
                hidden
              />
            </label>
            <span className="text-[11px] text-parchment-100/50">
              Generated {new Date(manifest.generatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
      </section>
    </>
  );
}
